/**
 * Administration.
 *
 * ═══════════════════════════════════════════════════════════════════
 * THE ORGANISATION CONFIGURATION IS THE PRODUCT'S BACKGROUND IP MADE VISIBLE.
 *
 * Everything on the configuration tab is data. Change the terminology and every
 * screen in the application changes its words. Change the colours and the whole
 * interface rebrands. Switch off a module and its navigation disappears — for
 * everybody, because the server stops serving it too.
 *
 * There is no "switch organisation" control here, and there deliberately is not
 * one. Which organisation a deployment serves is decided when its database is
 * built. A production user cannot reach another customer's configuration
 * because this instance has never heard of it.
 * ═══════════════════════════════════════════════════════════════════
 */

import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTenant } from '../lib/tenant';
import { dateTime, statusLabel, statusTone } from '../lib/format';
import { Alert, AsyncPanel, Badge, Card, DataTable, DefinitionList, EmptyState, PageHeader, Tabs } from '../components/ui';

interface Workflow {
  id: string;
  code: string;
  name: string;
  description: string;
  subject_type: string;
  is_active: boolean;
  sla_hours: number;
  stages?: Array<{ id: string; stage_no: number; name: string; approver_type: string; min_value_minor: string; max_value_minor: string | null; prevents_self_approval: boolean }>;
}

interface SegregationView {
  rules: Array<{
    code: string;
    name: string;
    description: string;
    severity: string;
    conflicts: Array<{ userId: string; fullName: string; roles: string }>;
  }>;
  cleanRuleCount: number;
}

interface ProfileInfo {
  activeProfile: string;
  available: Array<{ key: string; label: string; entities: number }>;
  switchMechanism: string;
  environment: string;
}

export function AdminPage() {
  const { can } = useSession();
  const { configuration, branding, terminology } = useTenant();
  const [tab, setTab] = useState('configuration');

  const workflows = useAsync(
    () => (tab === 'workflows' ? api.get<{ workflows: Workflow[] }>('/operations/workflows') : Promise.resolve({ workflows: [] })),
    [tab],
  );
  const segregation = useAsync(
    () =>
      tab === 'segregation' && can('audit.view')
        ? api.get<SegregationView>('/operations/security/segregation')
        : Promise.resolve(null as never),
    [tab],
  );
  const profiles = useAsync(
    () =>
      tab === 'configuration' && can('platform.tenant.view')
        ? api.get<ProfileInfo>('/platform/profiles')
        : Promise.resolve(null as never),
    [tab],
  );

  return (
    <>
      <PageHeader title="Administration" subtitle="Configuration, workflows and the segregation of duties." />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'configuration', label: 'Organisation configuration' },
          { key: 'workflows', label: 'Approval workflows' },
          { key: 'segregation', label: 'Segregation of duties' },
        ]}
      />

      {tab === 'configuration' ? (
        <div className="stack">
          <Alert tone="info" title="This is configuration, not code">
            <p>
              Every value below is data held against this organisation. The application has one codebase and one
              accounting engine; what makes it this organisation's system is this configuration.
            </p>
          </Alert>

          <div className="grid grid--2">
            <Card title="Identity">
              <DefinitionList
                items={[
                  ['Organisation', branding.organisationName],
                  ['Product name', branding.productName],
                  ['Strapline', branding.productTagline],
                  ['Monogram', branding.monogram],
                  ['Provider', branding.providerLine ?? '—'],
                  ['Environment', configuration?.environmentLabel ?? '—'],
                ]}
              />
              <div className="row" style={{ gap: '0.35rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                {Object.entries(branding.colours).map(([name, value]) => (
                  <span
                    key={name}
                    title={`${name}: ${value}`}
                    style={{
                      width: '2rem',
                      height: '2rem',
                      borderRadius: '4px',
                      background: value,
                      border: '1px solid rgba(0,0,0,0.15)',
                      display: 'inline-block',
                    }}
                  >
                    <span className="visually-hidden">
                      {name}: {value}
                    </span>
                  </span>
                ))}
              </div>
            </Card>

            <Card title="Words this organisation uses" subtitle="Every screen asks for these rather than hard-coding a noun.">
              <DefinitionList
                items={[
                  ['Organisation', terminology.organisation],
                  ['Legal entity', `${terminology.entity} / ${terminology.entityPlural}`],
                  ['Central unit', terminology.centralUnit],
                  ['Department', terminology.department],
                  ['Cost centre', terminology.costCentre],
                  ['Budget holder', terminology.budgetHolder],
                  ['Entity leader', terminology.entityLeader],
                  ['Requisition', terminology.requisition],
                  ['Purchase order', terminology.purchaseOrder],
                  ['Consolidated view', terminology.consolidated],
                ]}
              />
            </Card>

            <Card title="Modules" subtitle="A module that is off is off on the server too, not merely hidden.">
              <div className="row" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
                {Object.entries(configuration?.modules ?? {}).map(([key, on]) => (
                  <Badge key={key} tone={on ? 'ok' : 'neutral'}>
                    {statusLabel(key)}
                    {on ? '' : ' (off)'}
                  </Badge>
                ))}
              </div>
            </Card>

            <Card title="Features">
              <div className="row" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
                {Object.entries(configuration?.features ?? {}).map(([key, on]) => (
                  <Badge key={key} tone={on ? 'ok' : 'neutral'}>
                    {statusLabel(key)}
                    {on ? '' : ' (off)'}
                  </Badge>
                ))}
              </div>
            </Card>

            <Card title="Financial settings">
              <DefinitionList
                items={[
                  [
                    'Financial year starts',
                    `${configuration?.financial.yearStartDay ?? 1} / month ${configuration?.financial.yearStartMonth ?? '—'}`,
                  ],
                  [
                    'Approval bands',
                    (configuration?.financial.approvalBands ?? []).map((b) => `£${b.toLocaleString('en-GB')}`).join(' · '),
                  ],
                  ['Capitalisation threshold', `£${(configuration?.financial.capitalisationThresholdPounds ?? 0).toLocaleString('en-GB')}`],
                  ['Default payment terms', `${configuration?.financial.defaultPaymentTermsDays ?? 0} days`],
                  ['Currency', configuration?.locale.currency ?? 'GBP'],
                ]}
              />
            </Card>

            <Card title="Structure" subtitle="How this organisation is shaped.">
              <ul className="stack stack--sm">
                {(configuration?.structure ?? []).map((level) => (
                  <li key={level.key}>
                    <span className="strong">{level.label}</span>
                    <span className="smaller muted"> — {level.description ?? statusLabel(level.maps)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {profiles.data ? (
            <Card title="Which configuration is loaded" subtitle="Read only. Choosing one is a deployment action.">
              <DefinitionList
                items={[
                  ['Active profile', <span className="mono">{profiles.data.activeProfile}</span>],
                  ['Environment', profiles.data.environment],
                  [
                    'Profiles this build ships',
                    profiles.data.available.map((p) => `${p.label} (${p.entities} entities)`).join(', '),
                  ],
                ]}
              />
              <p className="smaller muted" style={{ marginTop: '0.75rem' }}>
                {profiles.data.switchMechanism}
              </p>
            </Card>
          ) : null}
        </div>
      ) : tab === 'workflows' ? (
        <Card title="Approval workflows" subtitle="Value bands, approver types and the self-approval bar." flush>
          <AsyncPanel state={workflows}>
            {(data) => (
              <DataTable
                caption="Approval workflows"
                rows={data.workflows}
                rowKey={(w) => w.id}
                emptyMessage="No workflows are configured."
                columns={[
                  { key: 'code', header: 'Code', render: (w) => <span className="mono smaller">{w.code}</span> },
                  {
                    key: 'name',
                    header: 'Workflow',
                    render: (w) => (
                      <>
                        <div>{w.name}</div>
                        <div className="smaller muted">{w.description}</div>
                      </>
                    ),
                  },
                  { key: 'subject', header: 'Applies to', render: (w) => statusLabel(w.subject_type) },
                  {
                    key: 'stages',
                    header: 'Stages',
                    render: (w) =>
                      w.stages && w.stages.length > 0 ? (
                        <ol className="smaller" style={{ margin: 0, paddingLeft: '1.1rem' }}>
                          {w.stages.map((s) => (
                            <li key={s.id}>
                              {s.name}{' '}
                              <span className="muted">
                                ({statusLabel(s.approver_type)}
                                {Number(s.min_value_minor) > 0
                                  ? `, above £${(Number(s.min_value_minor) / 100).toLocaleString('en-GB')}`
                                  : ''}
                                )
                              </span>
                              {s.prevents_self_approval ? null : (
                                <Badge tone="warn">Self-approval allowed</Badge>
                              )}
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <span className="muted smaller">—</span>
                      ),
                  },
                  { key: 'sla', header: 'SLA', render: (w) => <span className="smaller">{w.sla_hours} hours</span> },
                  { key: 'active', header: 'Active', render: (w) => <Badge tone={w.is_active ? 'ok' : 'neutral'}>{w.is_active ? 'Active' : 'Inactive'}</Badge> },
                ]}
              />
            )}
          </AsyncPanel>
        </Card>
      ) : (
        <AsyncPanel state={segregation}>
          {(data) =>
            !data ? (
              <EmptyState title="You do not have access to the segregation report" />
            ) : (
              <div className="stack">
                <Alert tone="info" title="What this report is, and what it is not">
                  <p>
                    It lists people holding combinations of permissions that could, in principle, let one person
                    complete a transaction end to end. It is not the only control: the engine separately refuses
                    self-approval, refuses an invoice authorised by the person who entered it, and refuses a payment run
                    released by the person who prepared it — regardless of what permissions anybody holds.
                  </p>
                </Alert>

                <Card title="Segregation rules" flush>
                  <DataTable
                    caption="Segregation of duties rules and any conflicts"
                    rows={data.rules}
                    rowKey={(r) => r.code}
                    columns={[
                      { key: 'code', header: 'Rule', render: (r) => <span className="mono smaller">{r.code}</span> },
                      {
                        key: 'name',
                        header: 'What it prevents',
                        render: (r) => (
                          <>
                            <div>{r.name}</div>
                            <div className="smaller muted">{r.description}</div>
                          </>
                        ),
                      },
                      { key: 'severity', header: 'Severity', render: (r) => <Badge tone={statusTone(r.severity)}>{statusLabel(r.severity)}</Badge> },
                      {
                        key: 'conflicts',
                        header: 'People affected',
                        render: (r) =>
                          r.conflicts.length === 0 ? (
                            <Badge tone="ok">None</Badge>
                          ) : (
                            <ul className="smaller" style={{ margin: 0, paddingLeft: '1.1rem' }}>
                              {r.conflicts.map((c) => (
                                <li key={c.userId}>
                                  {c.fullName} <span className="muted">({c.roles})</span>
                                </li>
                              ))}
                            </ul>
                          ),
                      },
                    ]}
                  />
                </Card>
              </div>
            )
          }
        </AsyncPanel>
      )}
    </>
  );
}
